import React from "react";

import { get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ErrorMessage from "./ErrorMessage";
import FieldContainer from "./FieldContainer";
import Label from "./Label";
import { containerClasses, inputClasses, wrapperClasses } from "./styles";
import CopyToClipboardButton from "../utils/CopyToClipboardButton";

import type { JSX } from "react";

import type { InputProps } from "./types";

export default function TextInput({
  icon,
  name,
  label,
  sublabel,
  type = "text",
  isRequired = false,
  isReadOnly = false,
  minLength = 0,
  defaultValue,
  className,
  isCompact,
  min,
  isNoWrap,
  validate,
  onBlurCustom,
  isTooltipErrorMessage,
  placeholder,
  hasCopyToClipboard = false,
  prefix,
  isFullWidth,
  normalize,
}: InputProps): JSX.Element {
  const {
    register,
    getFieldState,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();
  const { t } = useTranslation("editor-errors");
  const fieldValue = watch(name);

  const isInvalid = getFieldState(name)?.invalid;
  const error = get(errors, name);

  const { onBlur, onChange, ...rest } = register(name, {
    required: isRequired && t("requiredField"),
    minLength,
    min: min && {
      value: min,
      message: t("minValue", {
        min,
      }),
    },
    valueAsNumber: type === "number",
    validate,
  });

  return (
    <FieldContainer
      className={className}
      isCompact={isCompact}
      isFullWidth={isFullWidth}
      isNoWrap={isNoWrap}
    >
      {label ? (
        <Label
          isCompact={isCompact}
          isFullWidth={isFullWidth}
          isRequired={isRequired}
          label={label}
          name={name}
          sublabel={sublabel}
        />
      ) : null}
      <div className={wrapperClasses({ isCompact })}>
        <div className={containerClasses()}>
          <div className="text-muted-foreground pointer-events-none absolute inset-y-0 left-0 flex items-center pl-[0.46875rem]">
            {icon}
          </div>
          {prefix ? (
            <span className="bg-muted text-muted-foreground inline-flex h-[2.1875rem] shrink-0 items-center rounded-l-lg px-[0.46875rem] text-[0.78125rem]">
              {prefix}
            </span>
          ) : null}
          <input
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...rest}
            data-tooltip-content={error?.message}
            data-tooltip-id={`field-${name}-tooltip`}
            data-tooltip-position-strategy="fixed"
            defaultValue={defaultValue}
            disabled={isReadOnly}
            placeholder={placeholder}
            type={type}
            className={inputClasses({
              icon,
              isInvalid,
              isReadOnly,
              hasCopyToClipboard,
              hasPrefix: !!prefix,
            })}
            onBlur={(e): void => {
              onBlur(e);
              onBlurCustom?.(e);
            }}
            onChange={(e): void => {
              if (normalize) {
                setValue(name, normalize(e.target.value), {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              } else {
                onChange(e);
              }
            }}
          />
          {hasCopyToClipboard && fieldValue ? (
            <CopyToClipboardButton value={fieldValue} />
          ) : null}
        </div>
        <ErrorMessage
          error={error}
          isCompact={isCompact}
          isTooltip={isTooltipErrorMessage}
          name={name}
        />
      </div>
    </FieldContainer>
  );
}
